import { format } from '@querodelivery/qd-date-helper';
import isEmpty from 'lodash/isEmpty';
import { Loader } from '@quero-delivery/quero-components-web';
import { createPortal } from 'react-dom';

import useModal from '@/hooks/util/useModal';
import { paymentMethods } from '@/static/FranchiseConstants';
import useAddressModal from '@/hooks/address/useAddressModal';
import { convertMetersToKilometers, moneyLabel } from '@/Utils/FnUtils';
import useUf from '@/hooks/address/useUf';
import { useAppDispatch, useAppSelector } from '@/_store/hooks';
import styles from './styles.module.scss';
import { hideFranchiseModal } from '@/_store/modules/franchise';
import { useGetCityFareById } from '@/api/fares/get-city-fare-by-id';
import { FARE_OF_TYPE_LABEL } from '@/features/fare/types';
import type { PaymentMethodsTypes } from '@/Interfaces/Data';

function FranchiseModal() {
  const Modal = useModal({ maxHeight: undefined });

  const dispatch = useAppDispatch();

  const fareToShow = useAppSelector(state => state.franchises.fareToShow);
  const fareIdToShow = useAppSelector(state => state.franchises.fareIdToShow);

  const { data: fareData, isFetching } = useGetCityFareById(fareIdToShow, {
    enabled: !!fareIdToShow,
  });

  const actuallyFareToShow = fareToShow ?? fareData;

  const { getUfInfo } = useUf();

  const uf = getUfInfo(fareToShow?.uf || '');

  function closeModal() {
    dispatch(hideFranchiseModal());
  }

  const { showAddressModal } = useAddressModal();

  if (!actuallyFareToShow && !isFetching) {
    return null;
  }

  const formattedFareOfType = actuallyFareToShow?.fareOfType
    ? FARE_OF_TYPE_LABEL[actuallyFareToShow.fareOfType]
    : '';

  return createPortal(
    <Modal
      open
      onClose={closeModal}
      title="Detalhes da franquia"
      closeOnDimerClick
      loading={isFetching}
    >
      {isFetching ? (
        <div className="mgn-vtc-sm">
          <Loader position="center" />
        </div>
      ) : actuallyFareToShow ? (
        <table className={styles.tableContainer}>
          <tr>
            <span>Id</span>
            <strong>{actuallyFareToShow._id}</strong>
          </tr>
          <tr>
            <span>Nome</span>
            <strong>{actuallyFareToShow.nome}</strong>
          </tr>
          <tr>
            <span>Estado</span>
            <strong>{uf?.nome}</strong>
          </tr>
          <tr>
            <span>Código IBGE</span>
            <strong>{actuallyFareToShow.codigoIbge}</strong>
          </tr>
          <tr>
            <span>Localização</span>{' '}
            <a
              onClick={() =>
                showAddressModal({
                  location: actuallyFareToShow.localizacao,
                })
              }
            >
              <strong>Abrir</strong>
            </a>
          </tr>
          <tr>
            <span>Distância da entrega base</span>{' '}
            <strong>
              {convertMetersToKilometers(
                actuallyFareToShow.entregaBaseMts || 0,
              )}{' '}
              Km
            </strong>
          </tr>
          <tr>
            <span>Valor da entrega base</span>{' '}
            <strong>
              {moneyLabel(actuallyFareToShow.valorEntregaBase || 0)}
            </strong>
          </tr>
          <tr>
            <span>Distância limite para 2 entregas</span>{' '}
            <strong>
              {convertMetersToKilometers(
                actuallyFareToShow.maxDistanceDropsToGroup || 0,
              )}{' '}
              Km
            </strong>
          </tr>
          <tr>
            <span>Valor taxa Km adicional</span>{' '}
            <strong>
              {moneyLabel(actuallyFareToShow.taxaEntregaKmAdicional || 0)}
            </strong>
          </tr>
          <tr>
            <span>Distância entregador / estabelecimento</span>{' '}
            <strong>
              {convertMetersToKilometers(
                actuallyFareToShow.raioMaxDistance || 0,
              )}{' '}
              Km
            </strong>
          </tr>
          <tr>
            <span>Valor Km adicional</span>{' '}
            <strong>
              {moneyLabel(actuallyFareToShow.valorKmAdicional || 0)}
            </strong>
          </tr>
          <tr>
            <span>Quantidade de entregas por corrida</span>{' '}
            <strong>{actuallyFareToShow.qtdMaxEntregaGroup || 0}</strong>
          </tr>
          <tr>
            <span>
              Distância mínima do estabelecimento para concluir corrida
            </span>{' '}
            <strong>
              {convertMetersToKilometers(
                actuallyFareToShow.minDistanceCloseTo || 0,
              )}
            </strong>
          </tr>
          <tr>
            <span>Obrigatório entregador próximo ao estabelecimento?</span>{' '}
            <strong>
              {actuallyFareToShow.requiredNearPlaceToConfirm ? 'Sim' : 'Não'}
            </strong>
          </tr>
          <tr>
            <span>Quantidade de entregas por corrida</span>{' '}
            <strong>{actuallyFareToShow.qtdMaxEntregaGroup}</strong>
          </tr>
          <tr>
            <span>Data de criação</span>{' '}
            <strong>
              {format(
                new Date(actuallyFareToShow.createdAt || ''),
                'dd/MM/yyyy HH:mm',
              )}
            </strong>
          </tr>
          <tr>
            <span>Retorno obrigatório</span>{' '}
            <strong>
              {isEmpty(actuallyFareToShow.requiredReturnToConfirmPayMethods)
                ? 'Nenhum'
                : actuallyFareToShow.requiredReturnToConfirmPayMethods?.map(
                    (paymentForm, index) =>
                      `${index !== 0 ? ', ' : ''}${
                        paymentMethods[paymentForm as PaymentMethodsTypes]
                      }`,
                  )}
            </strong>
          </tr>
          <tr>
            <span>Tipo de tarifa</span> <strong>{formattedFareOfType}</strong>
          </tr>
        </table>
      ) : null}
    </Modal>,
    document.body,
  );
}

export default FranchiseModal;
